import { useTranslation } from 'react-i18next'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertTriangle, ArrowUp } from 'lucide-react'
import type { Layer } from '@/types/entities'

interface LayerInsertDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    layer: Layer | null
    onConfirm: () => void
    isLoading?: boolean
}

export function LayerInsertDialog({
    open,
    onOpenChange,
    layer,
    onConfirm,
    isLoading = false,
}: LayerInsertDialogProps) {
    const { t } = useTranslation()
    if (!layer) return null

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <ArrowUp className="h-5 w-5 text-primary" />
                        {t('layers.insertLayerTitle')}
                    </DialogTitle>
                    <DialogDescription>
                        {t('layers.confirmInsert')} {layer.layerID !== null ? `above Layer ID ${layer.layerID}` : 'at the beginning'}?
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    {/* Warning about layer IDs */}
                    <Alert>
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>
                            {t('layers.insertWarning')}
                        </AlertDescription>
                    </Alert>

                    {/* Layer Details */}
                    <div className="bg-muted p-3 rounded-lg">
                        <div className="text-sm space-y-1">
                            <div><strong>{t('layers.insertPosition')}:</strong> {t('layers.insertAbove')} Layer ID {layer.layerID !== null ? layer.layerID : '1'}</div>
                            {layer.description && (
                                <div><strong>{t('layers.currentDescription')}:</strong> {layer.description}</div>
                            )}
                        </div>
                    </div>
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={isLoading}
                    >
                        {t('common.cancel')}
                    </Button>
                    <Button
                        onClick={onConfirm}
                        disabled={isLoading}
                    >
                        {isLoading ? t('layers.inserting') : t('layers.insert')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}

